import React, { useState, memo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { isEmpty } from 'lodash';
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Typography,
} from '@mui/material';
import ListIcon from '@mui/icons-material/List';
import PlaylistAddCheckCircleIcon from '@mui/icons-material/PlaylistAddCheckRounded';
import { i18n } from '../../../translate/i18n';
import { orderQuestionArray } from '../../../utils/orderQuestionArray';
import { RootState } from '../../../store/types';
import { IGroup } from '../../../store/reducers/createService';
import Question from './Question';
import GroupHeader from './GroupHeader';
import ActionModal from './ActionModal';
import { IQuestion } from '../../../models/iquestion';
import { IAction } from '../../../models/iaction';
import * as actions from '../../../store/actions';
import { IServiceQuestion } from '../../../models/iserviceQuestion';
import { IUpdateServiceDraftBody } from '../CreateService/Checklist/Checklist';
import BadgeWithChildrenIcon from '../../../components/BadgeWithChildrenIcon';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import moment from 'moment';
import Spinner from '../../../components/Spinner';

type Props = {
  isNewData: boolean,
  editMode: boolean,
  mandatoryActionsForNotComplyQuestions: boolean,
  questionsRefs: any[],
  serviceId?: string,
  questionTree: IGroup<IQuestion | IServiceQuestion>[],
  checklistScore: string,
  goToActionPage: (actionId: string) => void,
  createOrUpdateDraft: (body: IUpdateServiceDraftBody) => Promise<void>,
}

const QuestionsList: React.FC<Props> = (props) => {
  const dispatch = useDispatch();
  const questionTree = props.questionTree;
  const [isNewAction, setIsNewAction] = useState<boolean>(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState<IQuestion | {}>({});
  const [selectedAction, setSelectedAction] = useState<IAction | null>(null);

  const isSuperAdmin = useSelector((state: any) => state.auth.superadmin);
  const loadingUpdateDraft = useSelector((state: RootState) => state.createService.updateServiceDraftLoading);
  const selectedDraft = useSelector((state: RootState) => state.createService.selectedDraft);
  const checklistScore = props.checklistScore;

  const handleOpenNewActionModal = (question: IQuestion) => {
    setSelectedQuestion(question);
    setIsNewAction(true);
    setIsModalOpen(true);
  }

  const handleOpenEditActionModal = (question: IQuestion, action: IAction) => {
    setSelectedAction(action);
    setSelectedQuestion(question);
    setIsNewAction(false);
    setIsModalOpen(true);
  }

  const handleAnswerAllQuestionsNa = () => {
    if (props.isNewData) dispatch(actions.createServiceAnswerAllQuestionsNa())
    else dispatch(actions.editServiceAnswerAllQuestionsNa())
  }
  return <>
    <ActionModal
      isNewData={isNewAction}
      isOpen={isModalOpen}
      onClose={() => setIsModalOpen(false)}
      question={selectedQuestion as IQuestion | IServiceQuestion}
      action={selectedAction}
      serviceId={props.serviceId}
      createOrUpdateDraft={props.createOrUpdateDraft}
    />
    <Box sx={styles.itensVerifContainer}>
      { isSuperAdmin && 
        <Button
          variant='outlined'
          onClick={handleAnswerAllQuestionsNa}
          disabled={!props.editMode && !props.isNewData}
        >
          Responder Todas as Perguntas com NA
        </Button>
      }
      
      <Box sx={styles.sectionTitleContainer}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <ListIcon sx={{ color: (!props.isNewData && !props.editMode) ? 'lightgrey' : '#000' }} />
          <Typography sx={{ ...styles.itensVerifLabel, color: (!props.isNewData && !props.editMode) ? 'lightgrey' : '#000' }}>
            {i18n.t('typographyFormServiceQuesList.itensVerifLabel') as string}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {(props.isNewData && selectedDraft) && <BadgeWithChildrenIcon
            bgColor={loadingUpdateDraft ? '#FFE8D6' : '#E6F4EA'}
            textColor={loadingUpdateDraft ? '#F57C00' : '#2E7D32'}
            text={loadingUpdateDraft ? i18n.t('createService.savingDraft') : `${i18n.t('createService.lastSavedDraft')} ${moment(selectedDraft.lastModifiedAt ? selectedDraft.lastModifiedAt : selectedDraft.startedAt).format('DD/MM/YYYY HH:mm')}`}
          >
            {loadingUpdateDraft ? <CircularProgress size={16} sx={{color: '#F57C00'}}/> : <CheckCircleIcon sx={{color:'#2E7D32', fontSize: 16}}/>}
          </BadgeWithChildrenIcon>}
          {!isEmpty(questionTree) &&
            <Box sx={styles.score}>
              <Box sx={styles.scoreText}>
                {checklistScore}
              </Box>
              <PlaylistAddCheckCircleIcon />
            </Box>
          }
        </Box>
      </Box>
      {!isEmpty(questionTree) && questionTree.map(group => 
        <Paper key={group.id} sx={styles.groupContainer}>
          <GroupHeader
            group={group}
          />
          {group.questionArray.sort(orderQuestionArray).map((question, index) => {

            const questionRef = React.createRef<Node>();
            const questionShouldHaveActions = (
              props.mandatoryActionsForNotComplyQuestions
              && question.answer === 'NO'
              && question.actions.length === 0
              && question.oldActions.length === 0
            );
            if (question.answer === '') props.questionsRefs.push(questionRef);
            else if (questionShouldHaveActions) {
              props.questionsRefs.push(questionRef);
            }

            return (
              <Question
                editMode={props.editMode}
                isNewData={props.isNewData}
                question={question}
                questionRef={questionRef}
                questionShouldHaveActions={questionShouldHaveActions}
                key={question._id}
                handleOpenNewActionModal={handleOpenNewActionModal}
                handleOpenEditActionModal={handleOpenEditActionModal}
                mandatoryActionsForNotComplyQuestions={props.mandatoryActionsForNotComplyQuestions}
                goToActionPage={props.goToActionPage}
                createOrUpdateDraft={props.createOrUpdateDraft}
              />
            );
          })}
        </Paper>
      )}
      </Box>
  </>
};

const styles = {
  itensVerifContainer: {
    marginBottom: '10px',
  },
  itensVerifLabel: {
    paddingLeft: '10px',
  },
  sectionTitleContainer: {
    display: 'flex',
    marginTop: '20px',
    position: 'relative',
    justifyContent: 'space-between',
  },
  formControl: {
    display: 'flex',
    marginTop: '10px',
    marginLeft: '10px',
    paddingLeft: '5px',
    paddingRight: '5px',
  },
  score: {
    paddingTop: '4px',
    paddingRight: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'row',
    marginLeft: '10px',
  },
  scoreText: {
    fontWeight: 'bold',
    marginRight: '8px'
  },
  fileError: {
    color: '#f44336'
  },
  buttonAction: {
    width: "10rem",
    minWidth: "10rem"
  },
  groupContainer: {
    borderRadius: '12px',
    paddingBottom: '10px',
  }
}

export default memo(QuestionsList);
